import { observer } from 'mobx-react-lite';
import React, { useState } from 'react'
import { Segment, Grid, Icon, Button, Header, Divider, Label, Statistic } from 'semantic-ui-react'
import { Game } from '../../../app/models/Game';
import { format } from 'date-fns'
import { GameLocationPreview } from '../../../components/map/GameLocationPreview';
import { useTranslation } from 'react-i18next';

interface Props {
    game: Game
}

export default observer(function GameDetailedInfo({ game }: Props) {
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);

    // Helper functions to get localized values
    const getLocalizedSport = (sportName: string) => {
        const key = `sports.${sportName.toLowerCase()}`;
        const translated = t(key);
        return translated === key ? sportName : translated;
    };

    const getLocalizedGameType = (gameTypeName: string) => {
        const key = `gameTypes.${gameTypeName.toLowerCase()}`;
        const translated = t(key);
        return translated === key ? gameTypeName : translated;
    };

    const getLocalizedPaymentType = (paymentType: string) => {
        const key = `gamePaymentTypes.${paymentType.toLowerCase()}`;
        const translated = t(key);
        return translated === key ? paymentType : translated;
    };

    const getLocalizedInclusion = (inclusionName: string) => {
        // Extract the key from the inclusion name (before the " - " part)
        const inclusionKey = inclusionName.split(' - ')[0].toLowerCase().replace(/\s+/g, '');
        const key = `inclusionTypes.${inclusionKey}`;
        const translated = t(key);
        return translated === key ? inclusionName : translated;
    };

    // Helper function to create Google Calendar URL
    const createGoogleCalendarUrl = () => {
        const startDate = format(game.dateTimeStart!, 'yyyyMMdd\'T\'HHmmss');
        const endDate = format(game.dateTimeEnd!, 'yyyyMMdd\'T\'HHmmss');
        const title = encodeURIComponent(`${getLocalizedSport(game.sport?.sportName || '')} Game`);
        const details = encodeURIComponent(game.gameDescription || '');
        const location = encodeURIComponent(`${game.latitude},${game.longitude}`);

        return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDate}/${endDate}&details=${details}&location=${location}`;
    };

    // Check if description is long enough to warrant a toggle
    const isLongDescription = game.gameDescription && game.gameDescription.length > 300;

    // Truncate description if it's too long and not expanded
    const displayDescription = isLongDescription && !isExpanded
        ? game.gameDescription.substring(0, 300) + '...'
        : game.gameDescription;

    return (
        <Segment.Group raised>
            {/* Header section */}
            <Segment attached='top' color='teal'>
                <Grid columns={2}>
                    <Grid.Column>
                        <Header as='h3'>
                            <Icon name='info circle' color='teal' />
                            <Header.Content>{t('gameDetailedInfo.gameDetails')}</Header.Content>
                        </Header>
                    </Grid.Column>
                    <Grid.Column textAlign='right'>
                        <Label color='blue' size='large'>
                            <Icon name='basketball ball' /> {getLocalizedSport(game.sport?.sportName || '')}
                        </Label>
                        <Label color={game.gamePaymentType?.paymentType === 'Free' ? 'green' : 'red'} size='large'>
                            <Icon name={game.gamePaymentType?.paymentType === 'Free' ? 'dollar sign' : 'credit card'} />
                            {getLocalizedPaymentType(game.gamePaymentType?.paymentType || '')}
                            {game.gamePrice > 0 && ` - $${game.gamePrice}`}
                        </Label>
                    </Grid.Column>
                </Grid>
            </Segment>

            {/* Hidden game warning */}
            {game.hiddenInSearch && (
                <Segment attached color='yellow' inverted secondary style={{ border: 'none' }}>
                    <Grid verticalAlign='middle'>
                        <Grid.Column width={1} textAlign='center'>
                            <Icon name='eye slash' size='large' />
                        </Grid.Column>
                        <Grid.Column width={15}>
                            <b>{t('gameDetailedInfo.privateGame')}</b> {t('gameDetailedInfo.privateGameDescription')}
                        </Grid.Column>
                    </Grid>
                </Segment>
            )}

            {/* Description section */}
            <Segment attached>
                <Header as='h4'>
                    <Icon name='file text' color='grey' />
                    <Header.Content>{t('gameDetailedInfo.description')}</Header.Content>
                </Header>
                <Divider />
                <div style={{
                    maxHeight: isExpanded ? 'none' : '200px',
                    overflow: 'hidden',
                    marginBottom: isLongDescription ? '10px' : '0'
                }}>
                    <p style={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        margin: '0'
                    }}>
                        {displayDescription}
                    </p>
                </div>

                {isLongDescription && (
                    <Button
                        size='tiny'
                        basic
                        color='teal'
                        onClick={() => setIsExpanded(!isExpanded)}
                        style={{ marginTop: '8px' }}
                    >
                        {isExpanded ? t('gameDetailedInfo.showLess') : t('gameDetailedInfo.readMore')}
                    </Button>
                )}
            </Segment>

            {/* Date and Time section */}
            <Segment attached>
                <Grid columns={2}>
                    <Grid.Column>
                        <Header as='h4'>
                            <Icon name='calendar' color='teal' />
                            <Header.Content>{t('gameDetailedInfo.dateTime')}</Header.Content>
                        </Header>
                    </Grid.Column>
                    <Grid.Column textAlign='left'>
                        <Button
                            size='medium'
                            color='google plus'
                            icon='calendar plus'
                            content={t('gameDetailedInfo.addToCalendar')}
                            onClick={() => window.open(createGoogleCalendarUrl(), '_blank')}
                            title={t('gameDetailedInfo.addToCalendar')}
                        />
                    </Grid.Column>
                </Grid>
                <Divider />
                <Grid>
                    <Grid.Column width={8}>
                        <Header as='h5' color='grey'>{t('gameDetailedInfo.startTime')}</Header>
                        <p><Icon name='calendar check' color='green' /> {format(game.dateTimeStart!, 'dd MMM yyyy')}</p>
                        <p><Icon name='clock' color='green' /> {format(game.dateTimeStart!, 'h:mm aa')}</p>
                    </Grid.Column>
                    <Grid.Column width={8}>
                        <Header as='h5' color='grey'>{t('gameDetailedInfo.endTime')}</Header>
                        <p><Icon name='calendar times' color='red' /> {format(game.dateTimeEnd!, 'dd MMM yyyy')}</p>
                        <p><Icon name='clock' color='red' /> {format(game.dateTimeEnd!, 'h:mm aa')}</p>
                    </Grid.Column>
                </Grid>
            </Segment>

            {/* Game details section */}
            <Segment attached>
                <Header as='h4'>
                    <Icon name='settings' color='teal' />
                    <Header.Content>{t('gameDetailedInfo.gameSettings')}</Header.Content>
                </Header>
                <Divider />
                <Grid columns={2} stackable>
                    <Grid.Column>
                        <Segment basic>
                            <Label ribbon color='blue'>{t('gameDetailedInfo.gameType')}</Label>
                            <p style={{ marginTop: '10px' }}>
                                <Icon name={game.gameType?.gameTypeName === 'Private' ? 'lock' : 'users'} />
                                {getLocalizedGameType(game.gameType?.gameTypeName || '')}
                            </p>
                        </Segment>
                    </Grid.Column>
                    <Grid.Column>
                        <Segment basic>
                            <Label ribbon color='purple'>{t('gameDetailedInfo.inclusion')}</Label>
                            <p style={{ marginTop: '10px' }}>
                                <Icon name='universal access' />
                                {getLocalizedInclusion(game.inclusion?.name || '')}
                            </p>
                        </Segment>
                    </Grid.Column>
                </Grid>

                <Grid columns={2} stackable>
                    <Grid.Column>
                        <Segment basic>
                            <Label ribbon color='orange'>{t('gameDetailedInfo.participants')}</Label>
                            <Statistic size='tiny' style={{ marginTop: '10px' }}>
                                <Statistic.Value>{game.gameParticipants?.length || 0}/{game.maxNumberOfParticipants}</Statistic.Value>
                                <Statistic.Label>{t('gameDetailedInfo.spotsFilledLabel')}</Statistic.Label>
                            </Statistic>
                        </Segment>
                    </Grid.Column>

                    <Grid.Column>
                        <Segment basic>
                            <Label ribbon color='green'>{t('gameDetailedInfo.contact')}</Label>
                            <Statistic size='tiny' style={{ marginTop: '10px' }}>
                                <Statistic.Value>{game.host?.fullName}</Statistic.Value>
                                <Statistic.Label>
                                    <Icon name='phone' /> {game.adminContactDetail}
                                    <span style={{ marginLeft: '10px' }}>
                                        <Icon
                                            name='whatsapp'
                                            color='green'
                                            link
                                            size='large'
                                            title='WhatsApp'
                                            onClick={() => window.open(
                                                `https://wa.me/${game.adminContactDetail.replace(/\D/g, '')}`,
                                                '_blank'
                                            )}
                                            style={{ cursor: 'pointer' }}
                                        />
                                        <button
                                            onClick={() => window.open(
                                                `viber://chat?number=${game.adminContactDetail.replace(/\D/g, '')}`,
                                                '_blank'
                                            )}
                                            title="Viber"
                                            style={{
                                                backgroundColor: '#7360F2',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: 6,
                                                padding: 4,
                                                cursor: 'pointer',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 28,
                                                height: 28,
                                                marginLeft: 6,
                                                verticalAlign: 'middle',
                                            }}
                                        >
                                            <Icon name="telegram plane" size="small" style={{ margin: 0 }} />
                                        </button>
                                    </span>
                                </Statistic.Label>
                            </Statistic>
                        </Segment>
                    </Grid.Column>
                </Grid>
            </Segment>

            {/* Location section */}
            <Segment attached>
                <Header as='h4'>
                    <Icon name='map marker alternate' color='teal' />
                    <Header.Content>
                        {t('gameDetailedInfo.location')}
                        <Header.Subheader style={{ marginTop: '0.3em', fontSize: '1.1em', color: 'rgba(0,0,0,0.6)' }}>
                            {t('gameDetailedInfo.distanceToYou', { distance: game.distanceToUserInKm })}
                        </Header.Subheader>
                    </Header.Content>
                </Header>
                <Button
                    size='small'
                    color='teal'
                    icon='external alternate'
                    content={t('gameDetailedInfo.openInGoogleMaps')}
                    onClick={() => window.open(`https://www.google.com/maps?q=${game.latitude},${game.longitude}`, '_blank')}
                    style={{ marginBottom: '1em' }}
                />
                <Divider />
                <GameLocationPreview
                    latitude={game.latitude}
                    longitude={game.longitude}
                />
            </Segment>
        </Segment.Group>
    )
})